import { L } from "../i18n";
// Rich content for every routed page — filled with R&H Lawyers LLP details.
import { company } from "../mock";

const solutionPages__raw = {
  business: {
    slug: "business",
    eyebrow: "For businesses & treasurers",
    title: "Protect and grow your business capital",
    intro:
      "Put idle corporate cash to work across a diversified panel of 55+ banks and building societies — with a single application, dual authorisation and full visibility for your finance team.",
    stat: { value: "4.66%", label: "blended average rate across the panel" },
    benefits: [
      {
        title: "One application, hundreds of products",
        body: "Replace weeks of onboarding and paperwork with a single application to access instant-access, notice and fixed-term deposits.",
      },
      {
        title: "Spread counterparty risk",
        body: "Diversify across multiple institutions to stay within FSCS limits and reduce concentration risk on your balance sheet.",
      },
      {
        title: "Dual authorisation & controls",
        body: "Set viewer, initiator and approver permissions so treasury movements always follow your internal governance.",
      },
      {
        title: "Consolidated reporting",
        body: "One statement, one login, one blended rate — with a tax pack ready for your accountants at year end.",
      },
    ],
    cta: "Open a business account",
  },
  personal: {
    slug: "personal",
    eyebrow: "For individuals & families",
    title: "Get more from your savings",
    intro:
      "Bring your savings together in one place, never lose sight of the interest you earn, and access market-leading rates without endless applications.",
    stat: { value: "55+", label: "banks & building societies on one platform" },
    benefits: [
      {
        title: "See everything in one place",
        body: "A single, elegant dashboard shows every deposit, rate and maturity date at a glance.",
      },
      {
        title: "FSCS peace of mind",
        body: "Spread funds across institutions to keep eligible deposits within FSCS protection limits.",
      },
      {
        title: "Move at the speed of the market",
        body: "Place and withdraw funds in minutes — no new forms each time rates move.",
      },
      {
        title: "A simple tax pack",
        body: "Receive a clear annual summary of interest earned for your self-assessment or adviser.",
      },
    ],
    cta: "Open a personal account",
  },
  charities: {
    slug: "charities",
    eyebrow: "For charities, schools & trusts",
    title: "Make your funding go further",
    intro:
      "Trustees have a duty to steward funds carefully. A diversified savings strategy helps your reserves earn more while remaining protected and accessible.",
    stat: { value: "£85k", label: "FSCS cover per eligible institution" },
    benefits: [
      {
        title: "Board-ready reporting",
        body: "Transparent statements and audit trails make it simple to report to trustees and governors.",
      },
      {
        title: "Multi-signatory authority",
        body: "Support the approval models your constitution requires, including dual and triple authorisation.",
      },
      {
        title: "Ethical & Sharia options",
        body: "Choose only from institutions that meet your investment policy, including Sharia-compliant providers.",
      },
      {
        title: "Access when you need it",
        body: "Balance instant-access liquidity with higher-yielding notice and fixed terms.",
      },
    ],
    cta: "Earn interest for your organisation",
  },
  accountants: {
    slug: "accountants",
    eyebrow: "For accountancy practices",
    title: "Grow your clients' assets",
    intro:
      "Strengthen client relationships by helping them earn more on cash. A seamless onboarding process and a dedicated point of contact make referrals effortless.",
    stat: { value: "1 form", label: "to access the entire panel of products" },
    benefits: [
      {
        title: "Add real value",
        body: "Help clients capture better rates and diversify risk — a straightforward win for their bottom line.",
      },
      {
        title: "Dedicated support",
        body: "A named relationship manager supports you and your clients through onboarding and beyond.",
      },
      {
        title: "Clean tax packs",
        body: "Consolidated interest statements make year-end and self-assessment simple.",
      },
      {
        title: "Custom rates for scale",
        body: "Larger deposits can access negotiated rates on request.",
      },
    ],
    cta: "Partner with R&H",
  },
  advisers: {
    slug: "advisers",
    eyebrow: "For financial advisers & wealth managers",
    title: "Support your clients' savings",
    intro:
      "Demonstrate added value by securing your clients' cash and helping them capitalise on their savings — with transparent reporting and responsive support.",
    stat: { value: "One login", label: "for your clients and their planners" },
    benefits: [
      {
        title: "Diversify counterparty risk",
        body: "Spread cash across institutions to manage risk and FSCS limits on your clients' behalf.",
      },
      {
        title: "Transparent & empowering",
        body: "Clients and planners share a clear view of holdings, rates and maturities.",
      },
      {
        title: "Multi-currency",
        body: "Hold selected foreign currencies and delay conversion until the timing is right.",
      },
      {
        title: "A responsive team",
        body: "Real people on hand when you and your clients need them.",
      },
    ],
    cta: "Support your clients",
  },
};

const infoPages__raw = {
  fscs: {
    eyebrow: "Deposit protection",
    title: "FSCS protection explained",
    intro:
      "The Financial Services Compensation Scheme (FSCS) protects eligible deposits held with UK-authorised banks and building societies if a firm fails.",
    sections: [
      {
        h: "How much is protected?",
        p: "Up to £85,000 per eligible depositor, per authorised institution (£170,000 for eligible joint accounts). Because our platform spans 55+ institutions, you can spread larger balances to keep more of your cash within FSCS limits.",
      },
      {
        h: "Which deposits are eligible?",
        p: "Eligibility depends on the depositor type and the institution's authorisation. Some banks share a single banking licence, which affects how the limit is applied — our team helps you structure deposits accordingly.",
      },
      {
        h: "Temporary high balances",
        p: "Certain life events (e.g. property sale, inheritance) may qualify for temporary additional protection of up to £1,000,000 for six months. Speak to us for details.",
      },
    ],
    note: "FSCS limits and eligibility are set by the FSCS. Figures are illustrative and do not constitute advice. See fscs.org.uk for full details.",
    cta: "Request an illustration",
  },
  currencies: {
    eyebrow: "Multi-currency",
    title: "Hold and earn in multiple currencies",
    intro:
      "Manage cross-border cash with confidence. Hold selected foreign currencies alongside sterling and convert on your own timeline.",
    sections: [
      {
        h: "Supported currencies",
        p: "GBP, EUR and USD accounts are available across selected panel institutions, with further currencies on request for larger balances.",
      },
      {
        h: "Convert when it suits you",
        p: "Especially helpful for clients with cross-border matters — delay conversion until the timing and rate are right for you.",
      },
      {
        h: "One consolidated view",
        p: "See all currency holdings, rates and maturities in a single dashboard with clear reporting.",
      },
    ],
    note: "Currency availability varies by client type and deposit size. Exchange is subject to prevailing rates.",
    cta: "Request an illustration",
  },
  security: {
    eyebrow: "Security & trust",
    title: "How we keep your funds and identity safe",
    intro:
      "As a firm regulated by the Solicitors Regulation Authority, stewardship and confidentiality sit at the heart of everything we do.",
    sections: [
      {
        h: "Segregated client monies",
        p: "Client monies are held separately in accordance with the SRA Accounts Rules. Deposits are placed directly with UK-authorised banks and building societies.",
      },
      {
        h: "Layered verification",
        p: "We operate robust identity verification, dual authorisation and continuous monitoring to protect against fraud and unauthorised access.",
      },
      {
        h: "Anti-money-laundering",
        p: `${company.legalName} is supervised for AML purposes by the SRA under the Money Laundering, Terrorist Financing and Transfer of Funds Regulations 2017, with policies, controls and procedures maintained accordingly.`,
      },
      {
        h: "Confidentiality",
        p: "We respect the privacy rights of every client and apply generally accepted professional standards to the protection of confidential information.",
      },
    ],
    note: "Regulated by the Solicitors Regulation Authority (Firm No. " + company.sraFirm + ").",
    cta: "Request an illustration",
  },
  about: {
    eyebrow: "About us",
    title: "A solicitors' firm built on trust and stewardship",
    intro: `${company.legalName} is a City of London solicitors' firm providing regulated legal services, now offering clients an elegant cash savings platform to make their money work harder.`,
    sections: [
      {
        h: "Who we are",
        p: `Incorporated in England and Wales on ${company.incorporated} as a ${company.legalForm} (Registration No. ${company.registration}), the firm is authorised and regulated by the ${company.regulator} (Firm No. ${company.sraFirm}). We are represented by ${company.representedBy}, Designated Member & Solicitor.`,
      },
      {
        h: "What we do",
        p: "Alongside our regulated legal practice, our savings platform gives clients access to competitive interest rates across a panel of 55+ banks and building societies — with diversification, FSCS protection on eligible deposits, and consolidated reporting.",
      },
      {
        h: "Our approach",
        p: "We combine the rigour and confidentiality of a regulated law firm with a modern, transparent platform — so individuals, businesses, charities and advisers can steward cash with confidence.",
      },
    ],
    note: `Registered office: ${company.address}.`,
    cta: "Talk to our team",
  },
  legal: {
    eyebrow: "Legal & privacy",
    title: "Legal, regulatory & privacy information",
    intro:
      "Important information about the firm, our regulation and how we handle your data.",
    sections: [
      {
        h: "Firm & regulation",
        p: `${company.legalName} is a ${company.legalForm} registered in England and Wales (Registration No. ${company.registration}), registered office ${company.address}. Authorised and regulated by the ${company.regulator} (Firm No. ${company.sraFirm}). Incorporated ${company.incorporated} and represented by ${company.representedBy}.`,
      },
      {
        h: "Privacy",
        p: "We process personal data lawfully, fairly and transparently, only for the purposes described to you, and retain it no longer than necessary. You have rights of access, rectification and erasure. Contact us to exercise your rights.",
      },
      {
        h: "Deposits & risk",
        p: "Deposits are placed with third-party UK-authorised banks and building societies. FSCS protection applies to eligible deposits subject to per-institution limits. Figures on this website are illustrative only and do not constitute financial, tax or legal advice.",
      },
      {
        h: "Complaints",
        p: `If something goes wrong, please contact us at ${company.email}. If we cannot resolve your complaint, you may be able to refer it to the relevant ombudsman.`,
      },
    ],
    note: "© " + new Date().getFullYear() + " " + company.legalName + ". All rights reserved.",
    cta: "Contact us",
  },
};

const careers__raw = {
  eyebrow: "Careers",
  title: "Build the future of cash management with us",
  intro:
    "We are a City of London solicitors' firm combining regulated legal rigour with a modern savings platform. We are always keen to hear from talented, values-driven people.",
  roles: [
    {
      title: "Client Relationship Manager",
      type: "Full-time · London EC3",
      body: "Own and grow relationships with individual, business and charity clients across the platform.",
    },
    {
      title: "Treasury & Operations Analyst",
      type: "Full-time · London EC3",
      body: "Support deposit placement, reconciliation and reporting across our panel of partner institutions.",
    },
    {
      title: "Compliance & AML Officer",
      type: "Full-time · London EC3",
      body: "Help maintain our SRA-regulated compliance framework and anti-money-laundering controls.",
    },
    {
      title: "Front-End Engineer",
      type: "Full-time · Hybrid",
      body: "Craft an elegant, accessible client experience across our web platform.",
    },
  ],
};

export const solutionPages = L(solutionPages__raw);
export const infoPages = L(infoPages__raw);
export const careers = L(careers__raw);
